// src/hooks/useUserTemplate.js
import { useEffect, useRef } from "react";

export const useUserTemplate = (
  mainId,
  userId,
  companyId,
  setFormData,
  templateData,
  onTemplateApplied,
) => {
  const onTemplateAppliedRef = useRef(onTemplateApplied);
  onTemplateAppliedRef.current = onTemplateApplied;

  const companyIdRef = useRef(companyId);
  companyIdRef.current = companyId;
  const lastAppliedCompanyIdRef = useRef(undefined);

  const deepMerge = (target = {}, source = {}) => {
    const output = { ...target };
    for (const key of Object.keys(source)) {
      if (
        source[key] &&
        typeof source[key] === "object" &&
        !Array.isArray(source[key])
      ) {
        output[key] = deepMerge(target[key] || {}, source[key]);
      } else {
        output[key] = source[key];
      }
    }
    return output;
  };

  useEffect(() => {
    const cid = companyIdRef.current;
    if (!templateData || !mainId || !userId || !cid) return;

    const { buyerSections } = templateData;

    const titleSettlementMeta = {
      ownersPolicy: true,
      lendersPolicy: true,
      escrowSettlement: true,
      escrowSettlementAmount: "0.00",
      ownersTitleAmount: "0.00",
      lendersTitleAmount: "0.00",
      discount: "0.00",
      discountAmount: "0.00",
    };

    const addressMeta = {
      address: "",
      unitNumber: "",
      street_no: "",
      street_name: "",
      city: "",
      state: "",
      state_code: "",
      county: "",
      zip: "",
      closing_state: "",
      closing_county: "",
      closing_zip_code: "",
    };

    const finalFormData = {
      addressMeta,
      addressVerified: false,
      preparedBy: "",
      preparedFor: "New Client",
      salesPrice: "0.00",
      escrowPremiumChanged: false,
      loanInfoMeta: {
        estimatedClosingDate: "",
        buyerDownPayment: "0.00",
        requestedDownPayment: "0.00",
        buyersLoanType: "Conventional",
        annualPropertyTax: "0.00",
        annualPropertyTaxAmount: "0.00",
        assessorsTaxRate: "0.00",
        autoEstimateLoan: false,
        conventionalPercent: "80.00",
        conventionalPercent2: "0.00",
        interestRate1:
          buyerSections?.tenure?.find((it) => it.key === "todays_interest_rate")
            ?.value ?? "0.00",
        interestRate2: "0.00",
        termsInYears1:
          buyerSections?.tenure?.find(
            (it) => it.key === "terms_of_loans_in_years",
          )?.value ?? "0",
        termsInYears2: "0",
        amount: "0.00",
        amount2: "0.00",
        adjusted: "0.00",
        noPMICheck: false,
        showAPRCheck: false,
        loansToBePaidBalance1: "0.00",
        loansToBePaidRate1: "0.00",
        loansToBePaidBalance2: "0.00",
        loansToBePaidRate2: "0.00",
        loansToBePaidBalance3: "0.00",
        loansToBePaidRate3: "0.00",
        totalloansToBePaid: "0.00",
        days: buyerSections?.tenure?.find((it) => it.key === "number_of_days_per_month")
            ?.value ?? "0.00",
        daysInterest: "0.00",
      },
      titleSettlementMeta,
      closingCostsMeta: [...(buyerSections?.closingCosts || [])],
      buyerClosingCostsMeta: [...(buyerSections?.conventional || [])],
      prepaidsMeta: {
        monthsTaxes: "0",
        rate:
          buyerSections?.insurance?.find(
            (it) => it.key === "tax_rate_per_year_per_100_of_sale_price",
          )?.value ?? "0.00",
        taxesAmount: "0.00",
        monthsIns:
          buyerSections?.insurance?.find(
            (it) =>
              it.key ===
              "homeowner_insurance_rate_per_year_per_1000_of_sale_price",
          )?.value ?? "0.00",
        rateThou:
          buyerSections?.insurance?.find(
            (it) => it.key === "number_of_months_insurance_prepaid",
          )?.value ?? "0.00",
        insAmount: "0.00",
        daysInterest: buyerSections?.tenure?.find((it) => it.key === "number_of_days_per_month")
            ?.value ?? "0.00",
        daysInterestAmount: "0.00",
        twoMonthsPMI: "0.00",
        fhaMip: "0.00",
        fhaMipCheck: true,
        fhaMipAmount: "0.00",
        FhaMipFin: "0.00",
        FhaMipFin2: "0.00",
        vaffFinance: "0.00",
        vaffFinanceCheck: true,
        vaffFinanceRate: "0.00",
        vaffFinanceAmount: "0.00",
        mipFinance: "0.00",
        mipFinanceCheck: true,
        mipFinanceAmount: "0.00",
        costs:
          buyerSections?.prepaid?.find((it) => it.key === "prepaid_other")
            ?.label ?? "Other",
        costsAmount:
          buyerSections?.prepaid?.find((it) => it.key === "prepaid_other")
            ?.value ?? "0.00",
        totalPrepaidItems: "0.00",
      },
      paymentMeta: {
        principalAndInterest: "0.00",
        realEstateTaxes: "0.00",
        homeownerInsurance: "0.00",
        rateMonthlyPMI: "0.00",
        rateMonthlyPMIAmount: "0.00",
        pAndI2ndTDAmount: "0.00",
        monthlyExpense1:
          buyerSections?.payments?.find((it) => it.key === "other_4")?.label ??
          "Other",
        monthlyExpense2:
          buyerSections?.payments?.find((it) => it.key === "hoa_4")?.label ??
          "Other",
        monthlyExpense1Amount:
          buyerSections?.payments?.find((it) => it.key === "other_4")?.value ??
          "0.00",
        monthlyExpense2Amount:
          buyerSections?.payments?.find((it) => it.key === "hoa_4")?.value ??
          "0.00",
        totalMonthlyPayment: "0.00",
        estimatedTaxProrations: "0.00",
      },
      totalClosingMeta: {
        totalInvestment: "0.00",
        totalCommission: "0.00",
        totalClosingCosts: "0.00",
        totalPrepaids: "0.00",
        totalMonthlyPayment: "0.00",
      },
    };

    setFormData((prev) => {
      const merged = deepMerge(finalFormData, prev);
      merged.closingCostsMeta = [...(buyerSections?.closingCosts || [])];
      merged.buyerClosingCostsMeta = [...(buyerSections?.conventional || [])];
      merged.loanInfoMeta = {
        ...merged.loanInfoMeta,
        interestRate1: finalFormData.loanInfoMeta.interestRate1,
        termsInYears1: finalFormData.loanInfoMeta.termsInYears1,
      };
      merged.prepaidsMeta = {
        ...merged.prepaidsMeta,
        rate: finalFormData.prepaidsMeta.rate,
        monthsIns: finalFormData.prepaidsMeta.monthsIns,
        rateThou: finalFormData.prepaidsMeta.rateThou,
        costs: finalFormData.prepaidsMeta.costs,
        costsAmount: finalFormData.prepaidsMeta.costsAmount,
      };
      merged.paymentMeta = {
        ...merged.paymentMeta,
        monthlyExpense1: finalFormData.paymentMeta.monthlyExpense1,
        monthlyExpense2: finalFormData.paymentMeta.monthlyExpense2,
        monthlyExpense1Amount: finalFormData.paymentMeta.monthlyExpense1Amount,
        monthlyExpense2Amount: finalFormData.paymentMeta.monthlyExpense2Amount,
      };

      onTemplateAppliedRef.current?.(merged);
      return merged;
    });

    lastAppliedCompanyIdRef.current = cid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateData, mainId, userId, companyId, setFormData]);
};
